import { infoBox } from '@core/components/AlertBox';
import { environment } from '@core/environment';
import { GrpcRestFetchTransport, useAuth0Interceptor } from '@deepup/api-utils';
import { CircleInformationOutline } from '@deepup/icons';
import {
  iorganizationManagementApiV1 as iorganizationManagementApi,
  iorganizationManagementClientApiV1 as iorganizationManagementClientApi,
} from '@deepup/internal-apis';
import {
  Divider,
  IconButton,
  List,
  ListItemButton,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import { buildDefaultListProjectInfosRequest } from '@projects/organization/buildDefaultListProjectInfosRequest';
import { projectSelectors } from '@projects/redux';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

export const useProjectMetadata = ({ projectId }: { projectId: string | undefined }) => {
  const [metadata, setMetadata] = useState<iorganizationManagementApi.ProjectInfo>();

  const authInterceptor = useAuth0Interceptor();
  const client = useMemo(() => {
    const transportRest = new GrpcRestFetchTransport({
      baseUrl: environment.unifiedApiUrl,
      fetchInit: {
        credentials: 'include',
      },
      interceptors: [authInterceptor],
    });

    return new iorganizationManagementClientApi.InternalOrganizationManagementServiceClient(
      transportRest,
    );
  }, [authInterceptor]);

  useEffect(() => {
    const fetchMetadata = async () => {
      if (!projectId) {
        return;
      }

      const project = await client.listProjectInfos(
        buildDefaultListProjectInfosRequest([projectId]),
      ).response;

      setMetadata(project.items.at(0));
    };
    fetchMetadata();
  }, [client, projectId]);

  return {
    metadata,
  };
};

const copyToClipboard = (key: string, text: string) => {
  navigator.clipboard.writeText(text);
  infoBox(`Copied ${key} to clipboard`);
};

const NameEmailSection = ({
  heading,
  name,
  email,
}: {
  heading: string;
  name: string | undefined;
  email: string | undefined;
}) => {
  if (!name && !email) {
    return null;
  }

  return (
    <>
      <Divider />
      <Stack>
        <Typography fontWeight="bold">{heading}</Typography>
        <List dense disablePadding>
          {!!name && (
            <ListItemButton onClick={() => copyToClipboard('name', name)}>
              <Stack direction="row" gap={1}>
                <Typography color="secondary">Name:</Typography>
                <Typography>{name}</Typography>
              </Stack>
            </ListItemButton>
          )}
          {!!email && (
            <ListItemButton onClick={() => copyToClipboard('E-Mail', email)}>
              <Stack direction="row" gap={1}>
                <Typography color="secondary">E-Mail:</Typography>
                <Typography>{email}</Typography>
              </Stack>
            </ListItemButton>
          )}
        </List>
      </Stack>
    </>
  );
};

export const MetadataInfoBox = () => {
  const { projectId } = useParams();
  const { metadata } = useProjectMetadata({ projectId });
  const project = useSelector(projectSelectors.selectById(projectId));
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  if (!project && !metadata) {
    return null;
  }

  return (
    <>
      <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
        <CircleInformationOutline fontSize={24} />
      </IconButton>
      <Popover
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Stack gap={2} minWidth={(theme) => theme.spacing(32)} padding={2}>
          <Stack gap={1}>
            <Stack direction="row" gap={1} alignItems="center">
              <CircleInformationOutline />
              <Typography fontWeight="bold">Project Info</Typography>
            </Stack>
            <Divider />
          </Stack>
          <Stack gap={1}>
            {!!project?.organizationName && (
              <Stack>
                <Typography fontWeight="bold">Organization</Typography>
                <List disablePadding dense>
                  <ListItemButton
                    onClick={() => copyToClipboard('organization', project.organizationName)}
                  >
                    <Typography>{project?.organizationName}</Typography>
                  </ListItemButton>
                </List>
              </Stack>
            )}
            <NameEmailSection
              heading="Customer Contact Person"
              name={metadata?.project?.contactPerson?.name}
              email={metadata?.project?.contactPerson?.email}
            />
            <NameEmailSection
              heading="Customer Manager"
              name={metadata?.customerManager?.name}
              email={metadata?.customerManager?.email}
            />
            <NameEmailSection
              heading="Project Manager"
              name={metadata?.internalContactPerson?.name}
              email={metadata?.internalContactPerson?.email}
            />
            <NameEmailSection
              heading="Vacation Replacement"
              name={metadata?.internalContactPersonVacationReplacement?.name}
              email={metadata?.internalContactPersonVacationReplacement?.email}
            />
          </Stack>
        </Stack>
      </Popover>
    </>
  );
};
